import * as React from "react";
import { SetStateAction, type JSX } from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { Skeleton, SxProps } from "@mui/material";
import { PaginationType } from "@/types/PaginationTypes";
import { HeadCell, Order, RowInterface } from "@/types/component/TableTypes";
import { EnhancedTableHead } from "./EnhancedTableHead";
import RichTooltip from "../common/RichTooltip";

interface EnhancedTableToolbarProps {
  numSelected: number;
  info?: React.ReactNode;
  toolbar?: boolean;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { numSelected, info, toolbar } = props;

  return (
    <>
      <Box
        key="infos-box"
        sx={{
          display: "flex",
          alignItems: "center",
          flexGrow: 1,
        }}
      >
        {info}
      </Box>
      {toolbar ? (
        <Toolbar
          sx={[
            {
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
            },
            numSelected > 0 && {
              bgcolor: (theme) =>
                alpha(
                  theme.palette.primary.main,
                  theme.palette.action.activatedOpacity
                ),
            },
          ]}
        >
          {numSelected > 0 ? (
            <Typography
              key="selected-box"
              sx={{ flex: "1 1 100%" }}
              color="inherit"
              variant="subtitle1"
              component="div"
            >
              <span>{numSelected}</span> <span>selected</span>
            </Typography>
          ) : null}

          {numSelected > 0 ? (
            <RichTooltip title="Delete" key="delete-tooltip">
              <IconButton>
                <DeleteIcon />
              </IconButton>
            </RichTooltip>
          ) : null}
        </Toolbar>
      ) : null}
    </>
  );
};

interface TableProps {
  headCells: HeadCell[];
  selectionColumn: boolean;
  rows: RowInterface[] | null;
  topCell?: React.ReactNode;
  bottomCell?: React.ReactNode;
  hover?: boolean;
  info?: React.ReactNode;
  /**
   * If not pagination is provided table will be unpaginated.
   */
  pagination?: PaginationType | undefined;
  rowsPerPage: number;
  setRowsPerPage: React.Dispatch<SetStateAction<number>>;
  page: number;
  setPage:
    | React.Dispatch<SetStateAction<number>>
    | ((pageNumber: number) => void);
  order: Order;
  setOrder: React.Dispatch<SetStateAction<Order>>;
  orderBy: string | null;
  setOrderBy: React.Dispatch<SetStateAction<string | null>>;
  rowsPerPageOptions?: number[];
  disableCount?: boolean;
  tableSx?: SxProps;
  containerPadding?: number;
  blankSlate?: JSX.Element;
  noDataText?: string;
}

export default function PaginatedListTable(props: TableProps) {
  const [selected, setSelected] = React.useState<readonly string[]>([]);

  const {
    headCells,
    selectionColumn,
    rows,
    info,
    topCell,
    bottomCell,
    rowsPerPage,
    pagination,
    setRowsPerPage,
    page,
    setPage,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    rowsPerPageOptions,
    disableCount,
    tableSx,
    containerPadding = 4,
    hover = true,
    noDataText = "No data to display",
  } = props;

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    event?.preventDefault();
    setPage(page);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  return (
    <Paper sx={{ p: containerPadding }}>
      <EnhancedTableToolbar info={info} numSelected={selected.length} />
      <TableContainer>
        <Table
          sx={{ minWidth: 750, ...tableSx }}
          aria-labelledby="tableTitle"
          size="medium"
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
            headCells={headCells}
            selectionColumn={selectionColumn}
            border={true}
          />
          <TableBody>
            {topCell}

            {rows && Boolean(rows.length === 0) && (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  padding="checkbox"
                  data-testid="paginated-list-table-col"
                >
                  {props.blankSlate ? (
                    props.blankSlate
                  ) : (
                    <Typography
                      component="div"
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 3, textAlign: "center" }}
                    >
                      {noDataText}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {rows
              ? rows.map((row, index) => {
                  const isItemSelected = isSelected(row.rowId);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover={hover}
                      onClick={
                        selectionColumn
                          ? (event) => handleClick(event, row.rowId)
                          : undefined
                      }
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      // Use mandatory id as row key.
                      key={row.rowId}
                      selected={isItemSelected}
                      data-testid="paginated-list-table-row"
                    >
                      {
                        //Loop over columns
                        Object.entries(headCells).map(([, cell]) => {
                          if (cell.id === "rowId") {
                            return (
                              <TableCell
                                padding="checkbox"
                                key={`cell-${row.rowId}-${cell.id}`}
                              >
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  inputProps={{
                                    "aria-labelledby": labelId,
                                  }}
                                />
                              </TableCell>
                            );
                          }

                          const rowEntry = row[cell.id];

                          return (
                            //Give cel uniqoue keys to prevent errrors.
                            <TableCell
                              data-testid="paginated-list-table-col"
                              key={`cell-${row.rowId}-${cell.id}`}
                              align={!cell?.numeric ? "left" : "right"}
                              sx={{ paddingLeft: 2 }}
                            >
                              {rowEntry}
                            </TableCell>
                          );
                        })
                      }
                    </TableRow>
                  );
                })
              : Array(rowsPerPage)
                  .fill(true)
                  .map((_, index) => index + 1)
                  .map((value) => (
                    <TableRow key={`shimmer-row-${value}`}>
                      <TableCell
                        key={`shimmer-cell-${value}`}
                        colSpan={headCells.length}
                        padding="checkbox"
                      >
                        <Skeleton
                          height="50px"
                          sx={{ bgcolor: "inherit" }}
                          animation="wave"
                        />
                      </TableCell>
                    </TableRow>
                  ))}

            {bottomCell}
          </TableBody>
        </Table>
      </TableContainer>
      {rows && pagination ? (
        <TablePagination
          sx={{
            ".MuiTablePagination-selectLabel": {
              color: "text.secondary",
            },
          }}
          rowsPerPageOptions={
            rowsPerPageOptions ? rowsPerPageOptions : [10, 25, 50, 100]
          }
          component="div"
          count={disableCount ? -1 : pagination ? pagination.count : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </Paper>
  );
}
